// PPS CRM - Dashboard Page
import { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { useNotificationStore } from '../stores/notifications';
import logoDark from '../assets/brand/paradox-logo-transparent.png';
import Clients from './Clients';
import Leads from './Leads';
import Tasks from './Tasks';
import Messages from './Messages';
import Surveys from './Surveys';
import Commissions from './Commissions';
import Documents from './Documents';
import Wallet from './Wallet';
import Settings from './Settings';
import Reminders from './Reminders';
import Analytics from './Analytics';
import CashFlow from './CashFlow';

const navigationByRole: Record<string, { name: string; href: string }[]> = {
  OWNER: [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Clients', href: '/dashboard/clients' },
    { name: 'Leads', href: '/dashboard/leads' },
    { name: 'Cash Flow', href: '/dashboard/cashflow' },
    { name: 'Analytics', href: '/dashboard/analytics' },
    { name: 'Tasks', href: '/dashboard/tasks' },
    { name: 'Surveys', href: '/dashboard/surveys' },
    { name: 'Commissions', href: '/dashboard/commissions' },
    { name: 'Messages', href: '/dashboard/messages' },
    { name: 'Documents', href: '/dashboard/documents' },
    { name: 'Wallet', href: '/dashboard/wallet' },
    { name: 'Reminders', href: '/dashboard/reminders' },
    { name: 'Settings', href: '/dashboard/settings' },
  ],
  MANAGER: [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Leads', href: '/dashboard/leads' },
    { name: 'Clients', href: '/dashboard/clients' },
    { name: 'Cash Flow', href: '/dashboard/cashflow' },
    { name: 'Analytics', href: '/dashboard/analytics' },
    { name: 'Tasks', href: '/dashboard/tasks' },
    { name: 'Surveys', href: '/dashboard/surveys' },
    { name: 'Commissions', href: '/dashboard/commissions' },
    { name: 'Messages', href: '/dashboard/messages' },
    { name: 'Documents', href: '/dashboard/documents' },
    { name: 'Wallet', href: '/dashboard/wallet' },
    { name: 'Reminders', href: '/dashboard/reminders' },
  ],
  AGENT: [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Leads', href: '/dashboard/leads' },
    { name: 'My Clients', href: '/dashboard/clients' },
    { name: 'Tasks', href: '/dashboard/tasks' },
    { name: 'Messages', href: '/dashboard/messages' },
    { name: 'Documents', href: '/dashboard/documents' },
    { name: 'Wallet', href: '/dashboard/wallet' },
    { name: 'Reminders', href: '/dashboard/reminders' },
  ],
};

export default function Dashboard() {
  const { user, logout } = useAuthStore();
  const { notifications, unreadCount, fetchNotifications, fetchUnreadCount } = useNotificationStore();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);

  const navigation = navigationByRole[user?.role || 'AGENT'];

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F6' }}>
      
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3" style={{ backgroundColor: '#000', color: '#fff' }}>
        <div className="flex items-center gap-4">
          {user?.agentCode && (
            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#DC2626' }}>{user.agentCode}</span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 rounded hover:bg-gray-800 transition-colors"
              style={{ color: '#fff' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: '#DC2626' }}>
                  {unreadCount}
                </span>
              )}
            </button>
            
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-xl z-50" style={{ backgroundColor: '#fff', border: '1px solid #E5E7EB' }}>
                <div className="p-3 border-b" style={{ borderColor: '#E5E7EB' }}>
                  <h3 className="font-semibold" style={{ color: '#111' }}>Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-center" style={{ color: '#6B7280' }}>No notifications</p>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div key={notif.id} className="p-3 border-b" style={{ borderColor: '#F3F4F6' }}>
                        <p className="font-medium text-sm" style={{ color: '#111' }}>{notif.title}</p>
                        {notif.body && <p className="text-sm" style={{ color: '#6B7280' }}>{notif.body}</p>}
                        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                          {new Date(notif.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium">{user?.fullLegalName}</div>
              <div className="text-xs" style={{ color: '#9CA3AF' }}>{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm px-3 py-1 rounded border"
              style={{ borderColor: '#4B5563', color: '#9CA3AF' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <nav className="sidebar w-56 flex-shrink-0">
          <div className="sidebar-brand">
            <img src={logoDark} alt="Paradox Payroll Systems" />
            <div className="sidebar-brand__tag">CRM</div>
          </div>
          <div className="p-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`nav-button mb-2 ${isActive ? 'nav-button--active' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route index element={<DashboardHome />} />
              <Route path="clients/*" element={<Clients />} />
              <Route path="leads/*" element={<Leads />} />
              <Route path="cashflow/*" element={<CashFlow />} />
              <Route path="analytics/*" element={<Analytics />} />
              <Route path="tasks/*" element={<Tasks />} />
              <Route path="surveys/*" element={<Surveys />} />
              <Route path="commissions/*" element={<Commissions />} />
              <Route path="messages/*" element={<Messages />} />
              <Route path="documents/*" element={<Documents />} />
              <Route path="wallet/*" element={<Wallet />} />
              <Route path="settings/*" element={<Settings />} />
              <Route path="reminders/*" element={<Reminders />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function DashboardHome() {
  const { user } = useAuthStore();
  
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#111' }}>
        Welcome back, {user?.fullLegalName}
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="rounded-lg p-6" style={{ backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #E5E7EB' }}>
          <h3 className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>Clients This Week</h3>
          <p className="text-3xl font-bold" style={{ color: '#DC2626' }}>0</p>
        </div>
        
        <div className="rounded-lg p-6" style={{ backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #E5E7EB' }}>
          <h3 className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>Active Clients</h3>
          <p className="text-3xl font-bold" style={{ color: '#059669' }}>0</p>
        </div>
        
        <div className="rounded-lg p-6" style={{ backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #E5E7EB' }}>
          <h3 className="text-sm font-medium mb-2" style={{ color: '#6B7280' }}>Commission This Week</h3>
          <p className="text-3xl font-bold" style={{ color: '#059669' }}>$0.00</p>
        </div>
      </div>
    </div>
  );
}
